/**
 * JC3 Payments — server-proxied integration with jc3wealthlegacy.com.
 *
 * The admin dashboard is single-user, so payment-site credentials live once
 * in `backend/.env`. This page just calls `/api/jc3-payments/*` and renders.
 *
 * If creds haven't been set yet, the backend returns HTTP 503 with an
 * actionable message; we render a compact "not connected" empty state
 * instead of any login form.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { PageHeader, Card, StatCard } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { API } from "../context/AuthContext";
import { relabel } from "../lib/displayNames";

const TOKEN_KEY = "bj_admin_token";
const tokenCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });

const money = (amount, currency = "USD") => {
  const n = typeof amount === "number" ? amount : parseFloat(amount || "0");
  return n.toLocaleString(undefined, { style: "currency", currency: currency || "USD" });
};

const STATUS_TONE = {
  captured: "text-[#6E5022]",
  settled: "text-[#14281E]",
  refunded: "text-[#6E5022]",
  voided: "text-[#5E4D40]",
  declined: "text-[#5C1A24]",
  error: "text-[#5C1A24]",
};
const StatusBadge = ({ status }) => (
  <span data-testid={`status-${status}`} className={`pr-tag ${STATUS_TONE[status] || STATUS_TONE.voided}`}>
    {status || "unknown"}
  </span>
);

const Toast = ({ kind, text, onDismiss }) => (
  <div
    data-testid={`jc3-payments-toast-${kind}`}
    className={`fixed bottom-6 right-6 z-50 max-w-sm px-4 py-3 border  ${
      kind === "ok"
        ? "border-[#14281E]/40 bg-[#DCCFB9] text-[#14281E]"
        : "border-[#5C1A24]/40 bg-[#DCCFB9] text-[#5C1A24]"
    }`}
  >
    <div className="flex items-start gap-2">
      <div className="flex-1 text-sm">{text}</div>
      <button data-testid="jc3-payments-toast-dismiss" onClick={onDismiss} className="text-xs underline decoration-[#C8A46A] underline-offset-4">Dismiss</button>
    </div>
  </div>
);



const NotConnected = ({ onRetry }) => (
  <Card data-testid="jc3-payments-not-connected" className="p-8 max-w-2xl mx-auto mt-8">
    <h2 className="font-display text-[#2A1C15] text-2xl mb-3">Not connected yet</h2>
    <p className="text-[#5E4D40] text-sm leading-relaxed mb-4">
      This tab reads live from your Authorize.net merchant account. Add these
      four deployment secrets and redeploy — transactions will appear here
      automatically.
    </p>
    <div className="bg-[#DCCFB9] px-4 py-3 text-xs font-mono text-[#2A1C15] mb-6 whitespace-pre overflow-x-auto">
{`AUTHNET_ENV                 production
AUTHNET_API_LOGIN_ID        your Authorize.net API Login ID
AUTHNET_TRANSACTION_KEY     your Authorize.net Transaction Key
AUTHNET_PUBLIC_CLIENT_KEY   your Accept.js Public Client Key`}
    </div>
    <button
      data-testid="jc3-payments-retry"
      onClick={onRetry}
      className="pr-btn"
    >
      Check again
    </button>
  </Card>
);

const PaymentsPage = () => {
  const [state, setState] = useState({ status: "loading" });
  const [refundingId, setRefundingId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const { data: statusData } = await axios.get(`${API}/jc3-payments/status`, tokenCfg());
      if (!statusData?.configured) {
        setState({ status: "not-connected" });
        return;
      }
      const { data } = await axios.get(`${API}/jc3-payments/transactions`, tokenCfg());
      const rows = (data.transactions || [])
        .slice()
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setState({ status: "ok", transactions: rows });
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message || "Unknown error";
      if (code === 503) {
        setState({ status: "not-connected" });
      } else {
        setState({ status: "error", detail });
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refund = async (t) => {
    const who = t.customer?.name || t.customer?.email || "customer";
    if (!window.confirm(`Refund ${money(t.amount, t.currency)} to ${who}?\n\nThis cannot be undone.\n(Unsettled charges will be voided instead.)`)) return;
    setRefundingId(t.trans_id);
    try {
      const { data } = await axios.post(`${API}/jc3-payments/refund`, {
        transaction_id: t.trans_id,
        last4: t.account_last4 || undefined,
      }, tokenCfg());
      const finalStatus = data.action || data.status || "refunded";
      setToast({ kind: "ok", text: `${money(t.amount, t.currency)} ${finalStatus === "voided" ? "voided" : "refunded"} to ${who}.` });
      await load();
    } catch (e) {
      setToast({ kind: "err", text: e?.response?.data?.detail || e.message || "Refund failed" });
    } finally {
      setRefundingId(null);
    }
  };

  const summary = useMemo(() => {
    const s = { settledRevenue: 0, counts: { captured: 0, settled: 0, refunded: 0, voided: 0, declined: 0 }, currency: "USD" };
    for (const t of (state.transactions || [])) {
      const amt = parseFloat(t.amount || "0");
      if (t.status in s.counts) s.counts[t.status] += 1;
      if (t.status === "settled") s.settledRevenue += amt;
      if (t.currency) s.currency = t.currency;
    }
    return s;
  }, [state.transactions]);

  return (
    <div data-testid="jc3-payments-page">
      <PageHeader
        eyebrow="DASHBOARD / JC3 PAYMENTS"
        title="JC3 Payments"
        subtitle="Live from Authorize.net — every charge processed through the dashboard. Refund or void from here."
      />

      {state.status === "loading" && <Loading label="Loading Authorize.net payments" />}
      {state.status === "not-connected" && <NotConnected onRetry={load} />}
      {state.status === "error" && <ErrorState message={state.detail} onRetry={load} />}

      {state.status === "ok" && (
        <>
          <div className="grid grid-cols-1 min-[960px]:grid-cols-3 xl:grid-cols-6 gap-5 mb-6">
            <StatCard testid="stat-settled-revenue" label="Settled Revenue" value={summary.settledRevenue} prefix="$" tone="green" />
            <StatCard testid="stat-count-captured" label="Captured"        value={summary.counts.captured} />
            <StatCard testid="stat-count-settled"  label="Settled"         value={summary.counts.settled}  tone="green" />
            <StatCard testid="stat-count-refunded" label="Refunded"        value={summary.counts.refunded} tone="amber" />
            <StatCard testid="stat-count-declined" label="Declined"        value={summary.counts.declined} tone="red" />
            <StatCard testid="stat-count-voided"   label="Voided"          value={summary.counts.voided} />
          </div>

          <Card data-testid="jc3-payments-table" className="overflow-x-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-[#2A1C15] text-lg">Transactions</h3>
                <span className="text-[#5E4D40] text-xs bj-tnum">({state.transactions.length})</span>
              </div>
              <button data-testid="jc3-payments-refresh" onClick={load} className="pr-btn-outline">
                Refresh
              </button>
            </div>

            <table className="w-full min-w-[1000px]">
              <thead>
                <tr className="text-left">
                  {["Date", "Customer", "Product", "Amount", "Card", "Status", "Action"].map((h) => (
                    <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.transactions.length === 0 && (
                  <tr><td colSpan={7} className="py-10 text-center text-[#5E4D40] text-sm">No transactions yet.</td></tr>
                )}
                {state.transactions.map((t, i) => {
                  const canRefund = !["refunded", "voided", "declined", "error"].includes(t.status);
                  const cust = t.customer || {};
                  return (
                    <tr key={t.trans_id || i} data-testid={`payment-row-${i}`} className="bj-table-row">
                      <td className="py-3 pr-3 text-[#5E4D40] text-xs bj-tnum whitespace-nowrap">
                        {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="py-3 pr-3 text-xs">
                        <div className="text-[#2A1C15]">{cust.name || "—"}</div>
                        <div className="text-[#5E4D40] bj-tnum">{cust.email || ""}</div>
                      </td>
                      <td className="py-3 pr-3 text-[#2A1C15] text-sm">{relabel(t.product_name || t.product_id) || "—"}</td>
                      <td className="py-3 pr-3 text-[#2A1C15] text-sm bj-tnum font-heading">{money(t.amount, t.currency)}</td>
                      <td className="py-3 pr-3 text-[#5E4D40] text-xs bj-tnum whitespace-nowrap">
                        {t.account_type || "—"} •••• {t.account_last4 || "----"}
                      </td>
                      <td className="py-3 pr-3"><StatusBadge status={t.status} /></td>
                      <td className="py-3 pr-3">
                        {canRefund ? (
                          <button
                            data-testid={`payment-refund-${i}`}
                            onClick={() => refund(t)}
                            disabled={refundingId === t.trans_id}
                            className="pr-btn-outline"
                          >
                            {refundingId === t.trans_id ? "Refunding…" : "Refund"}
                          </button>
                        ) : (
                          <span className="text-[#5E4D40] text-[0.75rem] tracking-widest uppercase font-heading">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {toast && <Toast kind={toast.kind} text={toast.text} onDismiss={() => setToast(null)} />}
    </div>
  );
};

export default PaymentsPage;
